import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, interval, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Store } from '@ngrx/store';
import { AppState } from '../app.reducer';
import { SetOrderActions } from '../shared/reducers/order.actions';
import { Order } from '../shared/models/order.model';

interface PendingOrder {
  order_id: string;
  created_at: number;
}

@Injectable({
  providedIn: 'root'
})
export class OrderStatusCheckerService {
  private checkInterval: Subscription;
  private readonly MAX_TIME_MS = 60 * 60 * 1000;
  private readonly CHECK_INTERVAL_MS = 30000;
  private readonly STORAGE_KEY = 'pending_order';

  constructor(
    private http: HttpClient,
    private store: Store<AppState>
  ) {}

  storePendingOrder(orderId: string): void {
    const pendingOrder: PendingOrder = {
      order_id: orderId,
      created_at: Date.now()
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(pendingOrder));
  }

  startOrderStatusCheck(): void {
    this.stopOrderStatusCheck();

    const pendingOrderData = localStorage.getItem(this.STORAGE_KEY);
    if (!pendingOrderData) {
      return;
    }

    try {
      const pendingOrder: PendingOrder = JSON.parse(pendingOrderData);

      const timeElapsed = Date.now() - pendingOrder.created_at;

      if (timeElapsed > this.MAX_TIME_MS) {
        this.clearPendingOrder();
        return;
      }

      this.checkInterval = interval(this.CHECK_INTERVAL_MS).subscribe(() => {
        this.checkOrderStatus(pendingOrder);
      });

      this.checkOrderStatus(pendingOrder);
    } catch (error) {
      console.error('Error parsing pending order data:', error);
      this.clearPendingOrder();
    }
  }

  stopOrderStatusCheck(): void {
    if (this.checkInterval) {
      this.checkInterval.unsubscribe();
      this.checkInterval = null;
    }
  }


  private checkOrderStatus(pendingOrder: PendingOrder): void {
    const timeElapsed = Date.now() - pendingOrder.created_at;

    if (timeElapsed > this.MAX_TIME_MS) {
      this.clearPendingOrder();
      this.stopOrderStatusCheck();
      return;
    }

    // NOTA: solo se conocen con certeza los códigos "no pagado" (0 y 6, documentados
    // más abajo). No hay documentación del código exacto de "pagado" en este repo (el
    // backend vive aparte), así que, para no vaciar el carrito de un pedido cancelado/
    // fallido con un código que no reconocemos, solo confirmamos el pago ante un código
    // explícitamente registrado como pagado. Un código desconocido se trata como "aún
    // no pagado": sigue reintentando hasta el timeout de 1h sin tocar el carrito.
    const KNOWN_NOT_PAID_STATUSES = [0, 6];
    const KNOWN_PAID_STATUSES: number[] = [];

    this.http.get(`${environment.apiUrl}/checkOrderStatus/${pendingOrder.order_id}`)
      .subscribe({
        next: (response: any) => {
			console.log('Respuesta del servidor:', response);
          if (response && response.status !== undefined) {
            if (KNOWN_PAID_STATUSES.includes(response.status)) {
				console.log('Pedido pagado exitosamente');
              this.handleSuccessfulPayment();
            } else {
				if (!KNOWN_NOT_PAID_STATUSES.includes(response.status)) {
					console.warn('checkOrderStatus devolvió un status no reconocido, se trata como "aún no pagado":', response.status);
				} else {
					console.log('Pedido no pagado aún');
				}
              const currentTimeElapsed = Date.now() - pendingOrder.created_at;
              if (currentTimeElapsed > this.MAX_TIME_MS) {
                this.clearPendingOrder();
                this.stopOrderStatusCheck();
                console.log('Verificación detenida: pedido no pagado después de 1 hora');
              }
            }
          }
        },
        error: (error) => {
          console.error('Error checking order status:', error);
        }
      });
  }


  private handleSuccessfulPayment(): void {
    this.clearPendingOrder();

    localStorage.removeItem('order');

    const action = new SetOrderActions(new Order());
    this.store.dispatch(action);

    this.stopOrderStatusCheck();

    console.log('Pedido pagado exitosamente - localStorage limpiado');
  }


  private clearPendingOrder(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }


  hasPendingOrder(): boolean {
    return localStorage.getItem(this.STORAGE_KEY) !== null;
  }


  getPendingOrder(): PendingOrder | null {
    const pendingOrderData = localStorage.getItem(this.STORAGE_KEY);
    if (!pendingOrderData) {
      return null;
    }

    try {
      return JSON.parse(pendingOrderData);
    } catch (error) {
      console.error('Error parsing pending order data:', error);
      this.clearPendingOrder();
      return null;
    }
  }
}
